import React, { useState, useMemo } from 'react';
import {
  Box, Button, TextField, InputAdornment, MenuItem, ToggleButton, ToggleButtonGroup, IconButton, Menu,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Snackbar, Alert, ListItemIcon, ListItemText, Tooltip
} from '@mui/material';
import {
  SearchRounded as SearchIcon, RefreshRounded as RefreshIcon, MoreVertRounded as MoreIcon,
  BuildRounded as FixIcon, CleaningServicesRounded as CleanIcon, SearchOffRounded as NoResultsIcon,
  EventBusyRounded as EmptyIcon
} from '@mui/icons-material';
import { apiCall } from '../config';
import { useApi, useLiveScores } from '../hooks';
import { PageHeader, CardGridSkeleton, EmptyState, ErrorState, cardGridSx, segmentedSx } from './ui';
import { MatchCard, PredictionDrawer } from './MatchCard';

const Predictions = () => {
  const { data, loading, error, reload } = useApi('/api/predictions');
  const { scoreFor } = useLiveScores();
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');
  const [league, setLeague] = useState('all');
  const [sort, setSort] = useState('probability');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'regenerate' | 'deep-clean'
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const leagues = useMemo(() => [...new Set((data || []).map(p => p.league))].sort(), [data]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data || [])
      .filter(p => league === 'all' || p.league === league)
      .filter(p => !q || `${p.home_team} ${p.away_team} ${p.league}`.toLowerCase().includes(q))
      .sort((a, b) => sort === 'kickoff'
        ? new Date(a.kickoff_time) - new Date(b.kickoff_time)
        : b.confidence - a.confidence);
  }, [data, query, league, sort]);

  const runAction = async (kind) => {
    setConfirm(null);
    setMenuAnchor(null);
    setBusy(true);
    const actions = {
      regenerate: { url: '/api/regenerate', pending: 'Regenerating predictions…', done: 'Predictions regenerated' },
      'fix-pending': { url: '/api/cleanup', pending: 'Fixing pending count…' },
      'deep-clean': { url: '/api/cleanup?remove_old=true', pending: 'Removing old data…' },
    };
    const action = actions[kind];
    setToast({ severity: 'info', message: action.pending });
    try {
      const response = await apiCall(action.url, { method: 'POST' });
      if (!response.ok) throw new Error();
      const result = await response.json();
      setToast({ severity: 'success', message: action.done || result.message || 'Done' });
      if (kind === 'regenerate') await reload();
    } catch {
      setToast({ severity: 'error', message: 'That didn\'t work. Try again in a moment.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Predictions"
        subtitle="Every match today with bookmaker odds. Probabilities are the market's view with the margin removed. Tap a match for the full breakdown."
        actions={
          <>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => setConfirm('regenerate')} disabled={busy} sx={{ flex: { xs: 1, sm: 'none' } }}>
              {busy ? 'Working…' : 'Regenerate'}
            </Button>
            <Tooltip title="Maintenance">
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} disabled={busy} aria-label="Maintenance actions" sx={{ border: 1, borderColor: 'divider', borderRadius: 2.5 }}>
                <MoreIcon />
              </IconButton>
            </Tooltip>
          </>
        }
      />

      <Box display="flex" gap={1.25} flexWrap="wrap" mb={2.5}>
        <TextField
          size="small"
          placeholder="Search teams or leagues"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ flex: { xs: '1 1 100%', sm: '1 1 260px' }, maxWidth: { sm: 360 } }}
        />
        <TextField select size="small" value={league} onChange={(e) => setLeague(e.target.value)} sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { sm: 200 } }}>
          <MenuItem value="all">All leagues</MenuItem>
          {leagues.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
        </TextField>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={sort}
          onChange={(_, v) => v && setSort(v)}
          sx={{ ...segmentedSx }}
        >
          <ToggleButton value="probability">Most likely</ToggleButton>
          <ToggleButton value="kickoff">Kickoff</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <CardGridSkeleton />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (data || []).length === 0 ? (
        <EmptyState
          icon={<EmptyIcon />}
          title="No predictions for today"
          body="Predictions are generated every morning at 9:00 UTC for matches that have bookmaker odds."
          action={<Button variant="outlined" onClick={() => setConfirm('regenerate')}>Generate now</Button>}
        />
      ) : visible.length === 0 ? (
        <EmptyState icon={<NoResultsIcon />} title="No matches found" body="Try a different search or league." />
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            {visible.length} {visible.length === 1 ? 'match' : 'matches'}
          </Typography>
          <Box sx={cardGridSx}>
            {visible.map(p => <MatchCard key={p.id} prediction={p} live={scoreFor(p)} onOpen={setSelected} />)}
          </Box>
        </>
      )}

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => runAction('fix-pending')}>
          <ListItemIcon><FixIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Fix pending count" secondary="Mark old unsettled matches as expired" />
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setConfirm('deep-clean'); }}>
          <ListItemIcon><CleanIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Deep clean" secondary="Delete old settled predictions" />
        </MenuItem>
      </Menu>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm === 'regenerate' ? 'Regenerate today\'s predictions?' : 'Delete old predictions?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirm === 'regenerate'
              ? `This replaces today's ${(data || []).length} predictions with fresh ones using the latest odds.`
              : 'Old settled predictions will be permanently removed from history. This can\'t be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)} color="inherit">Cancel</Button>
          <Button variant="contained" color={confirm === 'deep-clean' ? 'error' : 'primary'} onClick={() => runAction(confirm)}>
            {confirm === 'regenerate' ? 'Regenerate' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ bottom: { xs: 90, md: 24 } }}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>

      <PredictionDrawer prediction={selected} live={scoreFor(selected)} onClose={() => setSelected(null)} />
    </Box>
  );
};

export default Predictions;
